import i18n from 'i18n-js';
import { uiActions } from 'state/modules/UI';
import * as types from './types';
import * as vgActions from 'services/api/vehiclesGroups';
import * as organizationActions from 'services/api/organizations';
import * as vehiclesGroupsServices from 'services/api/vehiclesGroups';
import { getErrorMessage, parsePagination, formatVGServices, isCancel } from 'utils';
import { buildUrl } from 'utils';

const onExit = (payload) => ({
  type: types.SET_EXIT,
  payload,
});

export const setExit = (exit, type) => (dispatch) => {
  return dispatch(onExit({ exit, type }));
};

const onNextPosition = (payload) => ({
  type: types.CHANGE_POSITION,
  payload,
});

export const changePosition = (type, exit) => (dispatch, getState) => {
  if (exit) {
    return dispatch(setExit(exit, 'exitOnboarding'));
  }

  const {
    onboarding: { position },
  } = getState().vehiclesGroups;

  const nextPosition = type === 'next' ? 1 : -1;

  return dispatch(onNextPosition({ position: position + nextPosition }));
};

export const resetOnboarding =
  (payload = {}) =>
  (dispatch) => {
    return dispatch({ type: types.RESET_ONBOARDING, payload });
  };

export const resetVehicleGroups = () => (dispatch) => {
  return dispatch({ type: types.RESET_VEHICLE_GROUPS_OPTIONS });
};

const fetchCreateVehiclesGroupsRequest = () => ({
  type: types.FETCH_CREATE_VEHICLE_GROUPS_REQUEST,
});

const fetchCreateVehiclesGroupsSuccess = (payload) => ({
  type: types.FETCH_CREATE_VEHICLE_GROUPS_SUCCESS,
  payload,
});

const fetchCreateVehiclesGroupsFailed = (payload) => ({
  type: types.FETCH_CREATE_VEHICLE_GROUPS_FAILED,
  payload,
});

export const fetchCreateVehiclesGroups = (payload, step, exit) => async (dispatch, getState) => {
  const {
    onboarding: { steps },
  } = getState().vehiclesGroups;
  dispatch(fetchCreateVehiclesGroupsRequest());

  let response;
  if (steps?.details?.id) {
    response = await vgActions.updateVehiclesGroup(steps.details.id, payload);
  } else {
    response = await vgActions.createVehiclesGroups(payload);
  }

  if (response.ok) {
    dispatch(
      fetchCreateVehiclesGroupsSuccess({
        data: response.data,
        step,
      })
    );
    return dispatch(changePosition('next', exit));
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    dispatch(fetchCreateVehiclesGroupsFailed());

    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  let errors = {};

  formattedError?.length &&
    formattedError.map((val) => {
      return (errors[val.field] = val.message);
    });

  dispatch(fetchCreateVehiclesGroupsFailed({ errors }));
};

const fetchUpdateVehiclesGroupsRequest = () => ({
  type: types.FETCH_UPDATE_VEHICLE_GROUPS_REQUEST,
});

const fetchUpdateVehiclesGroupsSuccess = (payload) => ({
  type: types.FETCH_UPDATE_VEHICLE_GROUPS_SUCCESS,
  payload,
});

const fetchUpdateVehiclesGroupsFailed = (payload) => ({
  type: types.FETCH_UPDATE_VEHICLE_GROUPS_FAILED,
  payload,
});

export const fetchUpdateVehiclesGroups = (id, payload) => async (dispatch) => {
  dispatch(fetchUpdateVehiclesGroupsRequest());
  const response = await vgActions.updateVehiclesGroup(id, payload);

  if (response.ok) {
    dispatch(
      fetchUpdateVehiclesGroupsSuccess({
        data: response.data,
      })
    );
    dispatch(fetchVehicleGroupById(id, null));
    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('vehicle_groups.vehicle_groups_success_msg', { action: 'updated' }),
      })
    );
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    dispatch(fetchUpdateVehiclesGroupsFailed());

    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  let errors = {};

  formattedError?.length &&
    formattedError.map((val) => {
      return (errors[val.key] = val.message);
    });

  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: i18n.t('common.form_error'),
    })
  );

  dispatch(fetchUpdateVehiclesGroupsFailed({ errors }));
};

const updateStepListsRequest = (payload) => ({
  type: types.UPDATE_STEPS_LISTS_REQUEST,
  payload,
});

const updateStepListsSuccess = (payload) => ({
  type: types.UPDATE_STEPS_LISTS_SUCCESS,
  payload,
});

const updateStepListsFailed = (payload) => ({
  type: types.UPDATE_STEPS_LISTS_FAILED,
  payload,
});

export const fetchAvailableLocations =
  (params = {}, step = 'locations', listName = 'availables') =>
  async (dispatch) => {
    dispatch(updateStepListsRequest({ step, listName }));

    const response = await vgActions.getAvailableLocations(params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        updateStepListsSuccess({
          data: {
            list: response.data,
            pagination: pagination,
          },
          listName,
          step,
        })
      );
    }

    const formattedError = getErrorMessage(response.data);

    dispatch(updateStepListsFailed({ errors: formattedError, step, listName }));
  };

const parseVehicleGroupsLocations = (locations) => {
  const parsedData = locations?.map((val) => {
    return val.location;
  });
  return parsedData;
};

export const fetchVehiclesGroupsLocations =
  (id, step = 'locations', listName = 'assigned', params = {}) =>
  async (dispatch) => {
    dispatch(updateStepListsRequest({ step, listName }));

    const response = await vgActions.getVehiclesGroupsLocations(id, params);
    const pagination = parsePagination(response.headers);
    if (response.ok) {
      return dispatch(
        updateStepListsSuccess({
          data: {
            list: parseVehicleGroupsLocations(response.data),
            pagination,
          },
          listName,
          step,
        })
      );
    }

    const formattedError = getErrorMessage(response.data);
    dispatch(updateStepListsFailed({ errors: formattedError, step, listName }));
  };

const fetchAddItemToVehiclesGroupsRequest = () => ({
  type: types.FETCH_ADD_ITEM_TO_VEHICLES_GROUPS_REQUEST,
});

const fetchAddItemToVehiclesGroupsSuccess = () => ({
  type: types.FETCH_ADD_ITEM_TO_VEHICLES_GROUPS_SUCCESS,
});

const fetchAddItemToVehiclesGroupsFailed = () => ({
  type: types.FETCH_ADD_ITEM_TO_VEHICLES_GROUPS_FAILED,
});

export const fetchAddLocationToVehiclesGroups =
  (id, payload, step = 'locations', listName = 'assigned') =>
  async (dispatch) => {
    dispatch(fetchAddItemToVehiclesGroupsRequest());

    const response = await vgActions.addLocationToVehiclesGroups(id, payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('vehicle_groups.location_added_successfully'),
        })
      );
      dispatch(fetchVehiclesGroupsLocations(id, 'locations'));
      return dispatch(fetchAddItemToVehiclesGroupsSuccess());
    }

    const formattedError = getErrorMessage(response.data);

    dispatch(fetchAddItemToVehiclesGroupsFailed({ errors: formattedError }));

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          step,
          listName,
          type: 'error',
          message: formattedError,
        })
      );
    }
  };

const fetchRemoveLocationFromVehiclesGroupsRequest = (payload) => ({
  type: types.FETCH_REMOVE_ITEM_FROM_VEHICLES_GROUPS_REQUEST,
  payload,
});

const fetchRemoveLocationFromVehiclesGroupsSuccess = (payload) => ({
  type: types.FETCH_REMOVE_ITEM_FROM_VEHICLES_GROUPS_SUCCESS,
  payload,
});

const fetchRemoveLocationFromVehiclesGroupsFailed = (payload) => ({
  type: types.FETCH_REMOVE_ITEM_FROM_VEHICLES_GROUPS_FAILED,
  payload,
});

export const fetchRemoveLocationFromVehiclesGroups = (id, locationId) => async (dispatch) => {
  dispatch(fetchRemoveLocationFromVehiclesGroupsRequest());

  const response = await vgActions.removeLocationFromVehiclesGroups(id, locationId);

  if (response.ok) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('vehicle_groups.location_removed_successfully'),
      })
    );
    dispatch(fetchVehiclesGroupsLocations(id, 'locations'));
    return dispatch(fetchRemoveLocationFromVehiclesGroupsSuccess());
  }

  const formattedError = getErrorMessage(response.data);

  dispatch(fetchRemoveLocationFromVehiclesGroupsFailed({ errors: formattedError }));

  if (typeof formattedError === 'string') {
    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }
};

export const fetchVehiclesGroupsServices =
  (id, step = 'services', listName = 'assigned') =>
  async (dispatch) => {
    dispatch(updateStepListsRequest({ step, listName }));

    const response = await vgActions.getVehicleGroupById(id);
    // TO DO get data from state
    const orgResponse = await organizationActions.fetchOrganizations();
    if (orgResponse.ok && response.ok) {
      const formattedData = formatVGServices(response.data.children, [...orgResponse.data]);

      return dispatch(
        updateStepListsSuccess({
          data: { list: formattedData },
          listName,
          step,
        })
      );
    }

    const formattedError = getErrorMessage(!orgResponse.ok ? orgResponse.data : response.ok);
    dispatch(updateStepListsFailed({ errors: formattedError, step, listName }));

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }
  };

export const getAvailablesServices =
  (step = 'services', listName = 'availables', params) =>
  async (dispatch) => {
    const response = await organizationActions.fetchOrganizations(params);

    if (response.ok) {
      return dispatch(
        updateStepListsSuccess({
          data: { list: response.data },
          listName,
          step,
        })
      );
    }

    const formattedError = getErrorMessage(response.data);

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(fetchAddItemToVehiclesGroupsFailed({ errors: formattedError }));
  };

const fetchVehiclesGroupsRequest = (payload) => ({
  type: types.FETCH_VEHICLES_GROUPS_REQUEST,
  payload,
});

const fetchVehiclesGroupsSuccess = (payload) => ({
  type: types.FETCH_VEHICLES_GROUPS_SUCCESS,
  payload,
});

const fetchVehiclesGroupsFailed = (payload) => ({
  type: types.FETCH_VEHICLES_GROUPS_FAILED,
  payload,
});

/**
 * Get list of all vehicles groups
 * @param {object} params
 */
export const fetchVehicleGroups =
  (params, type = 'list', options) =>
  async (dispatch) => {
    dispatch(fetchVehiclesGroupsRequest(type));

    if (Object.prototype.hasOwnProperty.call(params, 'keyword')) {
      params.keyword = params.keyword || undefined;
    }

    const response = await vgActions.fetchVehiclesGroups(params, options);
    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchVehiclesGroupsSuccess({
          data: response.data,
          pagination,
          type,
        })
      );
    }

    if (!isCancel(response)) {
      const formattedError = getErrorMessage(response.data);

      if (typeof formattedError === 'string') {
        return dispatch(
          uiActions.handleShowSnackbar({
            type: 'error',
            message: formattedError,
          })
        );
      }

      dispatch(fetchVehiclesGroupsFailed({ formattedError, type }));
    }
  };

const fetchVehiclesGroupByIdRequest = (payload) => ({
  type: types.FETCH_VEHICLES_GROUP_BY_ID_REQUEST,
  payload,
});

const fetchVehiclesGroupByIdSuccess = (payload) => ({
  type: types.FETCH_VEHICLES_GROUP_BY_ID_SUCCESS,
  payload,
});

const fetchVehiclesGroupByIdFailed = (payload) => ({
  type: types.FETCH_VEHICLES_GROUP_BY_ID_FAILED,
  payload,
});

/**
 * Get vehicles group by id
 * @param {string} id Id of the service group
 */
export const fetchVehicleGroupById =
  (id, step = 'details', key = 'current') =>
  async (dispatch) => {
    dispatch(fetchVehiclesGroupByIdRequest({ step, key }));

    const response = await vgActions.fetchVehiclesGroupById(id);
    if (response.ok) {
      return dispatch(fetchVehiclesGroupByIdSuccess({ data: response.data, step, key }));
    }

    const formattedError = getErrorMessage(response.data);

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(fetchVehiclesGroupByIdFailed({ formattedError, step }));
  };

const fetchRelatedRulesetsRequest = (payload) => ({
  type: types.FETCH_RELATED_RULESETS_REQUEST,
  payload,
});

const fetchRelatedRulesetsSuccess = (payload) => ({
  type: types.FETCH_RELATED_RULESETS_SUCCESS,
  payload,
});

const fetchRelatedRulesetsFailed = (payload) => ({
  type: types.FETCH_RELATED_RULESETS_FAILED,
  payload,
});

/**
 * Get all rulesets availables for a vehicles group
 * @param {string} reference  Type of the rulesets
 */
export const fetchRelatedRulesets = (reference, params) => async (dispatch) => {
  dispatch(fetchRelatedRulesetsRequest());

  const response = await vgActions.fetchRelatedRuleset(reference, params);
  if (response.ok) {
    return dispatch(fetchRelatedRulesetsSuccess(response.data));
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    return dispatch(uiActions.handleShowSnackbar({ type: 'error', message: formattedError }));
  }

  dispatch(fetchRelatedRulesetsFailed({ formattedError }));
};

const fetchVehicleGroupsTimezonesRequest = (payload) => ({
  type: types.FETCH_VEHICLE_GROUPS_TIMEZONES_REQUEST,
  payload,
});

const fetchVehicleGroupsTimezonesSuccess = (payload) => ({
  type: types.FETCH_VEHICLE_GROUPS_TIMEZONES_SUCCESS,
  payload,
});

const fetchVehicleGroupsTimezonesFailed = (payload) => ({
  type: types.FETCH_VEHICLE_GROUPS_TIMEZONES_FAILED,
  payload,
});

/**
 * Get all rulesets availables for a vehicles group
 * @param {string} reference  Type of the rulesets
 */
export const fetchVehicleGroupsTimezones = (params) => async (dispatch) => {
  dispatch(fetchVehicleGroupsTimezonesRequest());

  const response = await vgActions.fetchVehicleGroupsTimezones(params);
  if (response.ok) {
    return dispatch(fetchVehicleGroupsTimezonesSuccess(response.data));
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  dispatch(fetchVehicleGroupsTimezonesFailed({ formattedError }));
};

const fetchRulesetToVehiclesGroupsRequest = (payload) => ({
  type: types.FETCH_RULESETS_TO_VEHICLES_GROUPS_REQUEST,
  payload,
});

const fetchRulesetToVehiclesGroupsSuccess = (payload) => ({
  type: types.FETCH_RULESETS_TO_VEHICLES_GROUPS_SUCCESS,
  payload,
});

const fetchRulesetToVehiclesGroupsFailed = (payload) => ({
  type: types.FETCH_RULESETS_TO_VEHICLES_GROUPS_FAILED,
  payload,
});

/**
 * Add or update a ruleset for a vehicles group
 * @param {string} groupId Id of the service group
 * @param {string} ruleset Id of the ruleset
 * @param {object} payload Payload for the request
 */
export const handleRulesetToVehiclesGroups =
  (groupId, ruleset, payload, resolve, reject) => async (dispatch) => {
    dispatch(fetchRulesetToVehiclesGroupsRequest());

    let response;
    if (ruleset) {
      response = await vgActions.updateRulesetToVehiclesGroups(groupId, ruleset, payload);
    } else {
      response = await vgActions.addRulesetToVehiclesGroups(groupId, payload);
    }
    if (response.ok) {
      dispatch(fetchRulesetToVehiclesGroupsSuccess());
      dispatch(fetchVehiclesGroupRulesets(groupId, 'pricing', resolve));
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: ruleset
            ? i18n.t('pricing_rules.ruleset_updated_successfully')
            : i18n.t('pricing_rules.ruleset_added_successfully'),
        })
      );
    }

    const formattedError = getErrorMessage(response.data);
    // Editable table plugin needs to return a promise on all methods, so in this case we need to resolve/reject the promise
    if (resolve) {
      reject();
    }
    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(fetchRulesetToVehiclesGroupsFailed({ formattedError }));
  };

/**
 * Remove pricing ruleset associated to a service group
 * @param {string} groupId Id of the service group
 * @param {string} ruleset Id of the ruleset
 * @param {object} payload Payload to remove the ruleset
 */
export const deleteRulesetFromVehiclesGroups =
  (groupId, ruleset, payload, resolve, reject) => async (dispatch) => {
    dispatch(fetchRulesetToVehiclesGroupsRequest());

    let response;
    try {
      response = await vgActions.deleteRulesetFromVehiclesGroups(groupId, ruleset, payload);
    } catch (err) {
      if (reject) reject();
      dispatch(fetchRulesetToVehiclesGroupsFailed({ formattedError: err?.message }));
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: err?.message || i18n.t('common.generic_error'),
        })
      );
    }

    if (response.status === 204) {
      dispatch(fetchRulesetToVehiclesGroupsSuccess());
      dispatch(fetchVehiclesGroupRulesets(groupId, 'pricing', resolve));
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('pricing_rules.ruleset_removed_successfully'),
        })
      );
    }

    const formattedError = getErrorMessage(response.data);
    // Editable table plugin needs to return a promise on all methods, so in this case we need to resolve/reject the promise
    if (reject) {
      reject();
    }

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(fetchRulesetToVehiclesGroupsFailed({ formattedError }));
  };

const fetchUpdateStepRequest = () => ({
  type: types.FETCH_UPDATE_STEP_REQUEST,
});

const fetchUpdateStepSuccess = (payload) => ({
  type: types.FETCH_UPDATE_STEP_SUCCESS,
  payload,
});

const fetchUpdateStepFailed = (payload) => ({
  type: types.FETCH_UPDATE_STEP_FAILED,
  payload,
});

/**
 * Get rulesets associated to current service group
 * @param {string} id Id of the service group
 * @param {string} step Current step where the rulesets are going to be stored
 * @param {string} step Current step where the rulesets are going to be stored
 */
export const fetchVehiclesGroupRulesets =
  (groupId, step = 'pricing', resolve) =>
  async (dispatch) => {
    dispatch(fetchUpdateStepRequest());

    const response = await vgActions.fetchVehiclesGroupRulesets(groupId);
    if (response.ok) {
      const rulesets = response.data;
      // The first ruleset is used as current and the rest are used on the pricing scheduled ruleset table
      const [first, ...rest] = rulesets;
      // Editable table plugin needs to return a promise on all methods, so in this case we need to resolve/reject the promise
      if (resolve) {
        resolve();
      }
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchUpdateStepSuccess({
          data: {
            currentRuleset: first,
            groupRulesets: rest,
            pagination,
          },
          step,
        })
      );
    }

    const formattedError = getErrorMessage(response.data);
    // Editable table plugin needs to return a promise on all methods, so in this case we need to resolve/reject the promise
    if (resolve) {
      resolve();
    }

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(fetchUpdateStepFailed({ errors: formattedError }));
  };

const fetchVehiclesGroupsActivationRequest = () => ({
  type: types.FETCH_VEHICLE_GROUPS_ACTIVATION_REQUEST,
});

const fetchVehiclesGroupsActivationSuccess = (payload) => ({
  type: types.FETCH_VEHICLE_GROUPS_ACTIVATION_SUCCESS,
  payload,
});

const fetchVehiclesGroupsActivationFailed = (payload) => ({
  type: types.FETCH_VEHICLE_GROUPS_ACTIVATION_FAILED,
  payload,
});

/**
 * Get rulesets associated to current service group
 * @param {object} payload Payload to remove the ruleset
 * @param {boolean} pristine Check if user has not interacted with the control yet.
 */
export const fetchVehiclesGroupsActivation = (payload, pristine) => async (dispatch, getState) => {
  const {
    onboarding: { steps },
  } = getState().vehiclesGroups;
  dispatch(fetchVehiclesGroupsActivationRequest());

  let response;
  if (pristine) {
    response = await vgActions.updateVehiclesGroup(steps.details.id, payload);
  }

  if (response?.ok || !pristine) {
    if (pristine) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: 'Service Group updated!',
        })
      );
    }
    return dispatch(fetchVehiclesGroupsActivationSuccess());
  }

  const formattedError = getErrorMessage(response?.data);
  dispatch(fetchVehiclesGroupsActivationFailed({ errors: formattedError }));

  if (typeof formattedError === 'string') {
    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: formattedError[0].message,
    })
  );
};

const fetchVehicleGroupsRRStimesRequest = () => ({
  type: types.FETCH_VEHICLE_GROUPS_RRS_TIMES_REQUEST,
});

const fetchVehicleGroupsRRStimesSuccess = (payload) => ({
  type: types.FETCH_VEHICLE_GROUPS_RRS_TIMES_SUCCESS,
  payload,
});

const fetchVehicleGroupsRRStimesFailed = (payload) => ({
  type: types.FETCH_VEHICLE_GROUPS_RRS_TIMES_FAILED,
  payload,
});

/**
 * Get rulesets times associated to current service group
 * @param {number} id Id of the current service group
 */
export const fetchVehicleGroupsRRStimes = (id, resolve) => async (dispatch) => {
  dispatch(fetchVehicleGroupsRRStimesRequest());

  let response = await vgActions.fetchVehicleGroupsRRStimes(id);

  // Editable table plugin needs to return a promise on all methods, so in this case we need to resolve/reject the promise
  if (resolve) {
    resolve();
  }

  if (response?.ok) {
    return dispatch(fetchVehicleGroupsRRStimesSuccess(response?.data));
  }

  const formattedError = getErrorMessage(response?.data);

  dispatch(fetchVehicleGroupsRRStimesFailed());

  if (typeof formattedError === 'string') {
    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: formattedError[0].message,
    })
  );
};

const fetchReservationRulesetToVehicleGroupRequest = () => ({
  type: types.FETCH_RESERVATION_RULESETS_TO_VEHICLES_GROUPS_REQUEST,
});

const fetchReservationRulesetToVehicleGroupSuccess = () => ({
  type: types.FETCH_RESERVATION_RULESETS_TO_VEHICLES_GROUPS_SUCCESS,
});

const fetchReservationRulesetToVehicleGroupFailed = () => ({
  type: types.FETCH_RESERVATION_RULESETS_TO_VEHICLES_GROUPS_FAILED,
});

/**
 * Add or update a ruleset for a vehicles group
 * @param {string} groupId Id of the service group
 * @param {string} ruleset Id of the ruleset
 * @param {object} payload Payload for the request
 */
export const handleReservationRulesetToVehiclesGroups =
  (groupId, ruleset, payload, resolve, reject) => async (dispatch) => {
    dispatch(fetchReservationRulesetToVehicleGroupRequest());

    let response;
    if (ruleset) {
      response = await vgActions.updateReservationRulesetToVehiclesGroups(
        groupId,
        ruleset,
        payload
      );
    } else {
      response = await vgActions.addReservationRulesetToVehiclesGroups(groupId, payload);
    }
    if (response.ok) {
      dispatch(fetchReservationRulesetToVehicleGroupSuccess());
      dispatch(fetchVehicleGroupsRRStimes(groupId, resolve));
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: ruleset
            ? i18n.t('vehicle_groups.reservation_ruleset_updated')
            : i18n.t('vehicle_groups.reservation_ruleset_added'),
        })
      );
    }

    const formattedError = getErrorMessage(response.data);
    // Editable table plugin needs to return a promise on all methods, so in this case we need to resolve/reject the promise
    if (resolve) {
      reject();
    }
    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(fetchReservationRulesetToVehicleGroupFailed({ formattedError }));
  };

const fetchDeleteReservationRulesetToVehicleGroupRequest = () => ({
  type: types.FETCH_DELETE_RESERVATION_RULESETS_TO_VEHICLES_GROUPS_REQUEST,
});

const fetchDeleteReservationRulesetToVehicleGroupSuccess = () => ({
  type: types.FETCH_DELETE_RESERVATION_RULESETS_TO_VEHICLES_GROUPS_SUCCESS,
});

const fetchDeleteReservationRulesetToVehicleGroupFailed = () => ({
  type: types.FETCH_DELETE_RESERVATION_RULESETS_TO_VEHICLES_GROUPS_FAILED,
});

/**
 * Remove pricing ruleset associated to a service group
 * @param {string} groupId Id of the service group
 * @param {string} ruleset Id of the ruleset
 * @param {object} payload Payload to remove the ruleset
 */
export const fetchdeleteReservationRulesetFromVehiclesGroups =
  (groupId, ruleset, resolve, reject) => async (dispatch) => {
    dispatch(fetchDeleteReservationRulesetToVehicleGroupRequest());

    let response;
    response = await vgActions.deleteReservationRulesetFromVehiclesGroups(groupId, ruleset);

    if (response.status === 204) {
      dispatch(fetchDeleteReservationRulesetToVehicleGroupSuccess());
      dispatch(fetchVehicleGroupsRRStimes(groupId, resolve));
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('vehicle_groups.reservation_ruleset_deleted'),
        })
      );
    }

    const formattedError = getErrorMessage(response.data);
    // Editable table plugin needs to return a promise on all methods, so in this case we need to resolve/reject the promise
    if (reject) {
      reject();
    }

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(fetchDeleteReservationRulesetToVehicleGroupFailed({ formattedError }));
  };

const fetchVehicleGroupsBHTimesRequest = () => ({
  type: types.FETCH_VEHICLE_GROUPS_BH_TIMES_REQUEST,
});

const fetchVehicleGroupsBHTimesSuccess = (payload) => ({
  type: types.FETCH_VEHICLE_GROUPS_BH_TIMES_SUCCESS,
  payload,
});

const fetchVehicleGroupsBHTimesFailed = (payload) => ({
  type: types.FETCH_VEHICLE_GROUPS_BH_TIMES_FAILED,
  payload,
});

/**
 * Get rulesets times associated to current service group
 * @param {number} id Id of the current service group
 */
export const fetchVehicleGroupsBHTimes = (id, resolve) => async (dispatch) => {
  dispatch(fetchVehicleGroupsBHTimesRequest());

  let response = await vgActions.fetchVehicleGroupsBHTimes(id);

  // Editable table plugin needs to return a promise on all methods, so in this case we need to resolve/reject the promise
  if (resolve) {
    resolve();
  }

  if (response?.ok) {
    return dispatch(fetchVehicleGroupsBHTimesSuccess(response?.data));
  }

  const formattedError = getErrorMessage(response?.data);

  dispatch(fetchVehicleGroupsBHTimesFailed());

  if (typeof formattedError === 'string') {
    return dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  dispatch(
    uiActions.handleShowSnackbar({
      type: 'error',
      message: formattedError[0].message,
    })
  );
};

const fetchBusinessHoursToVehiclesGroupsRequest = () => ({
  type: types.FETCH_BH_RULESETS_TO_VEHICLES_GROUPS_REQUEST,
});

const fetchBusinessHoursToVehiclesGroupsSuccess = () => ({
  type: types.FETCH_BH_RULESETS_TO_VEHICLES_GROUPS_SUCCESS,
});

const fetchBusinessHoursToVehiclesGroupsFailed = () => ({
  type: types.FETCH_BH_RULESETS_TO_VEHICLES_GROUPS_FAILED,
});

/**
 * Add or update a ruleset for a vehicles group
 * @param {string} groupId Id of the service group
 * @param {string} ruleset Id of the ruleset
 * @param {object} payload Payload for the request
 */
export const fetchBHRulesetToVehiclesGroups =
  (groupId, ruleset, payload, resolve, reject) => async (dispatch) => {
    dispatch(fetchBusinessHoursToVehiclesGroupsRequest());

    let response;
    if (ruleset) {
      response = await vgActions.updateBHRulesetToVehiclesGroups(groupId, ruleset, payload);
    } else {
      response = await vgActions.addBHRulesetToVehiclesGroups(groupId, payload);
    }
    if (response.ok) {
      dispatch(fetchBusinessHoursToVehiclesGroupsSuccess());
      dispatch(fetchVehicleGroupsBHTimes(groupId, resolve));
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: ruleset
            ? i18n.t('vehicle_groups.business_hours.bh_ruleset_updated')
            : i18n.t('vehicle_groups.business_hours.bh_ruleset_added'),
        })
      );
    }

    const formattedError = getErrorMessage(response.data);
    // Editable table plugin needs to return a promise on all methods, so in this case we need to resolve/reject the promise
    if (resolve) {
      reject();
    }
    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(fetchBusinessHoursToVehiclesGroupsFailed({ formattedError }));
  };

const fetchDeleteBHRulesetToVehicleGroupRequest = () => ({
  type: types.FETCH_DELETE_BH_RULESETS_TO_VEHICLES_GROUPS_REQUEST,
});

const fetchDeleteBHRulesetToVehicleGroupSuccess = () => ({
  type: types.FETCH_DELETE_BH_RULESETS_TO_VEHICLES_GROUPS_SUCCESS,
});

const fetchDeleteBHRulesetToVehicleGroupFailed = () => ({
  type: types.FETCH_DELETE_BH_RULESETS_TO_VEHICLES_GROUPS_FAILED,
});

/**
 * Remove pricing ruleset associated to a service group
 * @param {string} groupId Id of the service group
 * @param {string} ruleset Id of the ruleset
 * @param {object} payload Payload to remove the ruleset
 */
export const fetchDeleteBHRulesetFromVehiclesGroups =
  (groupId, ruleset, resolve, reject) => async (dispatch) => {
    dispatch(fetchDeleteBHRulesetToVehicleGroupRequest());

    let response;
    response = await vgActions.deleteBHRulesetFromVehiclesGroups(groupId, ruleset);

    if (response.status === 204) {
      dispatch(fetchDeleteBHRulesetToVehicleGroupSuccess());
      dispatch(fetchVehicleGroupsBHTimes(groupId, resolve));
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('vehicle_groups.business_hours.bh_ruleset_deleted'),
        })
      );
    }

    const formattedError = getErrorMessage(response.data);
    // Editable table plugin needs to return a promise on all methods, so in this case we need to resolve/reject the promise
    if (reject) {
      reject();
    }

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(fetchDeleteBHRulesetToVehicleGroupFailed({ formattedError }));
  };

// Feet Share/Unshare list

const fetchAvailableServicesRequest = (payload) => ({
  type: types.FETCH_AVAILABLE_SERVICES_REQUEST,
  payload,
});

const fetchAvailableServicesSuccess = (payload) => ({
  type: types.FETCH_AVAILABLE_SERVICES_SUCCESS,
  payload,
});

const fetchAvailableServicesFailed = (payload) => ({
  type: types.FETCH_AVAILABLE_SERVICES_FAILED,
  payload,
});

export const fetchAvailableServices = (params) => async (dispatch) => {
  dispatch(fetchAvailableServicesRequest());

  const response = await organizationActions.fetchOrganizations(params);

  if (response.ok) {
    const pagination = parsePagination(response.headers);
    return dispatch(fetchAvailableServicesSuccess({ data: response.data, pagination }));
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  dispatch(fetchAvailableServicesFailed({ errors: formattedError }));
};

// Feet Share/Unshare list

const fetchSharedServicesRequest = (payload) => ({
  type: types.FETCH_SHARED_SERVICES_REQUEST,
  payload,
});

const fetchSharedServicesSuccess = (payload) => ({
  type: types.FETCH_SHARED_SERVICES_SUCCESS,
  payload,
});

const fetchSharedServicesFailed = (payload) => ({
  type: types.FETCH_SHARED_SERVICES_FAILED,
  payload,
});

export const fetchSharedServices = (params) => async (dispatch) => {
  dispatch(fetchSharedServicesRequest());

  const response = await organizationActions.fetchOrganizations(params);

  if (response.ok) {
    const pagination = parsePagination(response.headers);
    return dispatch(fetchSharedServicesSuccess({ data: response.data, pagination }));
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  return dispatch(fetchSharedServicesFailed({ errors: formattedError }));
};

const shareFleetRequest = (payload) => ({
  type: types.FETCH_UPDATE_FLEET_REQUEST,
  payload,
});

const shareFleetSuccess = (payload) => ({
  type: types.FETCH_UPDATE_FLEET_SUCCESS,
  payload,
});

const shareFleetFailed = (payload) => ({
  type: types.FETCH_UPDATE_FLEET_FAILED,
  payload,
});

export const shareFleet = (id, payload) => async (dispatch) => {
  dispatch(shareFleetRequest());

  const response = await vgActions.addServiceToVehiclesGroups(id, payload);

  if (response.ok) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('vehicle_groups.service_added_successfully'),
      })
    );
    return dispatch(shareFleetSuccess());
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  return dispatch(shareFleetFailed({ errors: formattedError }));
};

const unshareFleetRequest = (payload) => ({
  type: types.FETCH_UPDATE_FLEET_REQUEST,
  payload,
});

const unshareFleetSuccess = (payload) => ({
  type: types.FETCH_UPDATE_FLEET_SUCCESS,
  payload,
});

/**
 * Get list of all vehicles groups
 * @param {object} params
 */
export const unshareFleet = (id, payload) => async (dispatch) => {
  dispatch(unshareFleetRequest(id));

  const response = await vgActions.unshareFleet(id, payload);
  if (response.ok) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('vehicle_groups.unshare_success'),
      })
    );
    return dispatch(
      unshareFleetSuccess({
        data: response.data,
      })
    );
  }

  const formattedError = getErrorMessage(response.data);

  if (typeof formattedError === 'string') {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  }

  return dispatch(shareFleetFailed({ errors: formattedError }));
};

const deleteVehicleGroupRequest = () => ({
  type: types.FETCH_DELETE_VEHICLE_GROUP_REQUEST,
});

const deleteVehicleGroupSuccess = (payload) => ({
  type: types.FETCH_DELETE_VEHICLE_GROUP_SUCCESS,
  payload,
});

const deleteVehicleGroupFailure = () => ({
  type: types.FETCH_DELETE_VEHICLE_GROUP_FAILURE,
});

export const deleteVehiceGroup = (groupId, history) => async (dispatch) => {
  try {
    dispatch(deleteVehicleGroupRequest());
    const result = await vehiclesGroupsServices.deleteVehicleGroup(groupId);
    if (!result.ok) throw new Error(i18n.t('vehicle_groups.delete_vehicle_group_error'));
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'success',
        message: i18n.t('vehicle_groups.delete_vehicle_group_success'),
      })
    );
    dispatch(deleteVehicleGroupSuccess());
    history.push(buildUrl('/service-groups'));
  } catch (error) {
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(error),
      })
    );
    dispatch(deleteVehicleGroupFailure());
  }
};
